import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  EventNote,
  Business,
  Build,
  Groups,
  Science,
  LocalShipping,
} from "@mui/icons-material";
import EventForm from "./EventForm";
import eventsService from "../../../api/eventsService";
import { buildIsoFromDateInput, formatDateInput } from "../utils";
import { dateToISO } from "../../../utils/dateUtils";
import { CALENDAR_EVENT_TYPES } from "../constants";

const EVENT_TYPE_ICONS = {
  OFFICE_VISIT: Business,
  MECHANIC_WORKSHOP: Build,
  MEETING: Groups,
  LAB_STUDY: Science,
  UNIT_DELIVERY: LocalShipping,
  MAINTENANCE: Build,
};

const INITIAL_FORM = {
  title: "Visita a oficina",
  eventType: "OFFICE_VISIT",
  date: "",
  hour: "",
  notes: "",
  location: "",
  technicians: [],
  status: "SCHEDULED",
};

export default function AddEventDialog({
  open = true,
  onClose,
  onSave,
  selectedDate,
  currentDate,
  technicians = [],
}) {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    if (selectedDate && currentDate) {
      const isoDate = buildIsoFromDateInput(null, selectedDate, currentDate);
      setFormData((prev) => ({
        ...prev,
        date: formatDateInput(isoDate),
      }));
    }
  }, [selectedDate, currentDate, open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (error) setError("");
  };

  const handleSubmit = async () => {
    if (!formData.date) {
      setError("Selecciona una fecha");
      return;
    }
    if (!formData.title?.trim()) {
      setError("Ingresa un título para el evento");
      return;
    }
    if (!formData.hour) {
      setError("Selecciona una hora");
      return;
    }
    if (!formData.technicians?.length) {
      setError("Asigna al menos un técnico para que aparezca en su procedia");
      return;
    }

    const technicianIds = formData.technicians
      .map((tech) => tech?._id || tech)
      .filter(Boolean);

    const dataToSend = {
      title: formData.title.trim(),
      eventType: formData.eventType,
      date: dateToISO(formData.date),
      hour: formData.hour,
      notes: formData.notes || "",
      location: formData.location || "",
      status: formData.status,
      technicians: technicianIds,
      technician: technicianIds[0] || null,
    };

    try {
      setSaving(true);
      await eventsService.create(dataToSend);
      if (onSave) await onSave();
      handleClose();
    } catch (err) {
      console.error("Error al crear evento:", err);
      setError(
        err.response?.data?.message ||
          "No se pudo crear el evento. Intenta de nuevo."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData(INITIAL_FORM);
    setError("");
    onClose();
  };

  const selectedEventType = CALENDAR_EVENT_TYPES.find(
    (et) => et.value === formData.eventType
  );
  const IconComponent =
    EVENT_TYPE_ICONS[formData.eventType] || EventNote;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          bgcolor: selectedEventType?.color || "primary.main",
          color: "white",
          py: 2.5,
          px: 3,
        }}
      >
        <IconComponent sx={{ fontSize: 32 }} />
        <Typography variant="h6" fontWeight={700}>
          Agregar evento
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, px: 3, pb: 2 }}>
        {error ? (
          <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
            {error}
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
            Esto no es un servicio. Se muestra en la procedia de cada técnico
            asignado, sin cliente ni reporte MIP.
          </Alert>
        )}
        <EventForm
          formData={formData}
          onChange={handleChange}
          technicians={technicians}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5, bgcolor: "grey.50" }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={saving}
          size="large"
          sx={{
            minWidth: 100,
            fontWeight: 600,
            textTransform: "none",
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          size="large"
          sx={{
            bgcolor: selectedEventType?.color,
            "&:hover": {
              bgcolor: selectedEventType?.color,
              filter: "brightness(0.9)",
            },
            minWidth: 140,
            fontWeight: 700,
            textTransform: "none",
            boxShadow: 3,
          }}
        >
          {saving ? <CircularProgress size={24} color="inherit" /> : "Guardar evento"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
